import React, { useState, useEffect } from 'react';
import { Button, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CustomInput from '../inputs/new-input';
import MultiSelect from '../inputs/multi-select';
import Modal from '../modal/modal';
import moment from 'moment';
import { roundValue } from 'src/util/decimal-utils';
import { formatDecimalTime } from '../../util/common';
import { ValidateField, ValidatorTypes } from '../../util/validators';

const TimeEntryForm = ({
    tasks,
    handleAddEntryToLocalList,
    currentEntries,
    handleRemoveEntryFromLocalList,
    modalOpen,
    handleCloseModal,
    handleSaveModal,
    editingContext,
    writePermission,
    onWarning
}) => {
    // Field-based state structure with validation
    const [fields, setFields] = useState({
        regular: {
            name: 'regular',
            type: 'number',
            label: 'Total hours',
            value: '0.00',
            touched: false,
            error: false,
            valid: true,
            validation: [ValidatorTypes.MIN],
            min: 0,
            max: 24,
            decimalPlaces: 2
        },
        task: {
            name: 'task',
            type: 'select',
            label: 'Task (optional)',
            value: null,
            touched: false,
            error: false,
            valid: true,
            validation: [ValidatorTypes.OPTIONAL]
        }
    });

    const [taskName, setTaskName] = useState('');
    const [taskPath, setTaskPath] = useState('');
    const [sectionName, setSectionName] = useState('');
    const [, setStageId] = useState('');
    const [editingEntryId, setEditingEntryId] = useState(null);
    const [hoursDisabled, setHoursDisabled] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    // Reset fields when modal opens/closes
    useEffect(() => {
        if (modalOpen) {
            resetFields();
        }
    }, [modalOpen]);

    const resetFields = () => {
        setFields(prev => ({
            ...prev,
            regular: { ...prev.regular, value: '0.00', touched: false, error: false, valid: true },
            task: { ...prev.task, value: null, touched: false, error: false, valid: true }
        }));
        setTaskName('');
        setTaskPath('');
        setSectionName('');
        setStageId('');
        setEditingEntryId(null);
        setHoursDisabled(false);
        setDeleteConfirmId(null);
    };

    const inputChangeHandler = (e) => {
        const { name, value } = e.target;
        const field = fields[name];
        const { error, valid } = ValidateField({ value, validation: field.validation });

        setFields(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                value,
                touched: true,
                error,
                valid
            }
        }));
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const roundedValue = roundValue(value, 2) || '0.00';
        const { error, valid } = ValidateField({ value: roundedValue, validation: fields[name].validation });

        setFields(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                value: roundedValue,
                touched: true,
                error,
                valid
            }
        }));
    };

    const handleTaskChange = (selectedTask) => {
        let path = '';
        let stageIdValue = '';
        let name = '';
        let section = '';

        if (selectedTask && selectedTask.value) {
            // Check if this is a stage allocation
            if (selectedTask.isStage === true) {
                stageIdValue = selectedTask.value;
                name = selectedTask.label || selectedTask.key || '';
                section = selectedTask.stagePath || name;
            } else {
                stageIdValue = '';
                name = selectedTask.label || selectedTask.key || '';
                // stagePath is set by convertToReactSelectFormat (e.g., "Stage > Substage")
                section = selectedTask.stagePath || '';
            }
            // Convert "Stage > Substage" to "Stage\Substage\TaskName"
            const stagePath = (selectedTask.stagePath || '').replace(/ > /g, '\\');
            path = stagePath ? `${stagePath}\\${name}` : name;
        }

        setFields(prev => ({
            ...prev,
            task: {
                ...prev.task,
                value: selectedTask,
                touched: true,
                error: false,
                valid: true
            }
        }));
        setTaskName(name);
        setTaskPath(path);
        setSectionName(section);
        setStageId(stageIdValue);
    };

    const validateForm = () => {
        let isValid = true;
        const updatedFields = { ...fields };

        const field = updatedFields.regular;
        const { error, valid } = ValidateField({ value: field.value, validation: field.validation });
        updatedFields.regular = { ...field, touched: true, error, valid };
        isValid = valid;

        setFields(updatedFields);
        return isValid;
    };

    const isAddTimeDisabled = () => {
        const reg = parseFloat(fields.regular.value || 0);
        return reg <= 0 || !writePermission;
    };

    const handleEditEntry = (entry) => {
        if (!writePermission) return;


        const taskOption = entry.stageId && entry.stageId !== ''
            ? tasks.find(t => t.value === entry.stageId && t.isStage === true) || null
            : tasks.find(t => t.value === entry.taskId) || null;

        setFields(prev => ({
            ...prev,
            regular: { ...prev.regular, value: roundValue(entry.regular || 0, 2), touched: false, error: false, valid: true },
            task: { ...prev.task, value: taskOption, touched: false, error: false, valid: true }
        }));
        setTaskName(entry.taskName || '');
        setTaskPath(entry.taskPath || '');

        const isSectionAttribution = entry.stageId && entry.stageId !== '';
        const sectionNameValue = entry.sectionName
            || (isSectionAttribution ? entry.taskName : null) // For sections, taskName contains section label
            || taskOption?.stagePath
            || '';
        setSectionName(sectionNameValue);
        setStageId(entry.stageId || '');
        setEditingEntryId(entry.id);

        // Check if there's a next entry (non-new) after this entry - disable hours if so
        const entryIndex = currentEntries.findIndex(e => e.id === entry.id);
        const nextEntry = currentEntries[entryIndex + 1];
        const hasNextNonNewEntry = nextEntry && !nextEntry.isNew;
        setHoursDisabled(hasNextNonNewEntry);
    };

    const handleAddTime = () => {
        if (!validateForm()) return;
        if (isAddTimeDisabled()) return;

        const reg = parseFloat(fields.regular.value || 0);

        // Check if single entry exceeds 24 hours
        if (reg > 24) {
            onWarning && onWarning(`A single entry cannot exceed 24 hours. You entered ${reg} hours.`);
            return;
        }

        // Calculate total hours for the day including existing entries
        const existingHours = currentEntries
            .filter(e => e.id !== editingEntryId)
            .reduce((acc, curr) => acc + (parseFloat(curr.regular) || 0), 0);

        const totalDailyHours = existingHours + reg;

        // Check if total daily hours exceed 24
        if (totalDailyHours > 24) {
            onWarning && onWarning(`Total daily hours cannot exceed 24 hours. Current entries: ${existingHours.toFixed(2)}h, New entry: ${reg.toFixed(2)}h, Total: ${totalDailyHours.toFixed(2)}h`);
            return;
        }

        const entryData = {
            regular: fields.regular.value,
            taskId: fields.task.value?.isStage ? '' : (fields.task.value?.value || ''),
            stageId: fields.task.value?.isStage ? (fields.task.value?.value || '') : '',
            taskName: taskName,
            taskPath: taskPath,
            sectionName: sectionName
        };

        // Pass editingEntryId for update, or null for new entry
        handleAddEntryToLocalList(entryData, editingEntryId);
        resetFields();
    };

    return (
        <Modal
            fullWidth={true}
            maxWidth='sm'
            save={<span data-cy="save-changes">Save</span>}
            cancel='Cancel'
            footerBtns={true}
            onClick={handleCloseModal}
            onEdit={handleSaveModal}
            className="time-entry-modal-root"
            open={modalOpen}
            onClose={handleCloseModal}
            disableBackdropClick={true}
            disableSave={!writePermission}
            modalHeading={
                editingContext?.userName ? (
                    <div className="modal-header-custom">
                        <span>Add Hours</span>
                        <span className="modal-subtitle">
                            {editingContext.userName} • {moment(editingContext.date).format('ddd, MMM D')}
                        </span>
                    </div>
                ) : "Add Hours"
            }
        >
            <div className="time-entry-modal-content">
                {!writePermission && (
                    <div className="alert alert-warning mb-3" role="alert" style={{ fontSize: '0.9rem' }}>
                        Please contact admin to add necessary permission to add or modify the time.
                    </div>
                )}
                <div className="time-entry-form-container">
                    {/* 1. Input Form Section (Top in Design) */}
                    <div className="add-entry-form">
                        <div className="modal-section-title">Add Time Entry</div>

                        {/* Hours Row */}
                        <div className="row mb-3">
                            <div className="col-6">
                                <CustomInput
                                    {...fields.regular}
                                    formatDecimals={true}
                                    customInputClass="text-right"
                                    onChange={inputChangeHandler}
                                    onBlur={handleBlur}
                                    disabled={hoursDisabled || !writePermission}
                                    dataCy="regular-hours"
                                />
                            </div>
                            <div className="col-6 d-flex align-items-center pt-3">
                                {fields.regular.value && (
                                    <span className="formatted-time-label">
                                        {formatDecimalTime(fields.regular.value)}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Task Row (Second in Design) */}
                        <div className="row mb-3">
                            <div className="col-md-12">
                                <MultiSelect
                                    label="Task (optional)"
                                    placeholder="Select a task"
                                    items={tasks}
                                    value={fields.task.value}
                                    onChangeHandler={handleTaskChange}
                                    isStage={true}
                                    isMulti={false}
                                    isSearchable={true}
                                    disabled={!writePermission}
                                    data-cy="task-select"
                                />
                            </div>
                        </div>

                        {/* Add/Update Button */}
                        <div className="d-flex justify-content-end gap-2">
                            {editingEntryId && (
                                <Button
                                    variant="contained"
                                    className="cancel-edit-btn btn-rounded"
                                    onClick={resetFields}
                                    data-cy="cancel-edit-btn"
                                >
                                    Cancel
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                className={`add-time-btn btn-rounded ${editingEntryId ? 'update-mode' : ''}`}
                                fullWidth={!editingEntryId}
                                onClick={handleAddTime}
                                disabled={isAddTimeDisabled()}
                                data-cy="add-time-btn"
                            >
                                {editingEntryId ? 'Update Time' : '+ Add Time'}
                            </Button>
                        </div>
                    </div>

                    {/* 2. Existing Entries Section (Bottom in Design) */}
                    {currentEntries.length > 0 && (
                        <div className="existing-entries">
                            <div className="entries-title">
                                <span className="dot"></span>
                                Added Entries ({currentEntries.length})
                            </div>
                            {currentEntries.map((entry) => {
                                const isApproved = entry.status === 'APPROVED';
                                const canEdit = writePermission && !isApproved;

                                return (
                                    <div
                                        key={entry.id}
                                        className={`entry-item ${editingEntryId === entry.id ? 'editing' : ''} ${canEdit ? 'clickable' : ''} ${isApproved ? 'approved' : ''}`}
                                        onClick={() => canEdit && handleEditEntry(entry)}
                                    >
                                        <div className="entry-info">
                                            <div className="hours-badges">
                                                <span className="hour-badge regular">
                                                    <AccessTimeIcon fontSize="inherit" />
                                                    {formatDecimalTime(entry.regular || 0)}
                                                </span>
                                                {isApproved && (
                                                    <span className="hour-badge approved-badge">
                                                        Approved
                                                    </span>
                                                )}
                                            </div>
                                            {(() => {
                                                const isSectionAttribution = entry.stageId && entry.stageId !== '';
                                                const isTaskAttribution = entry.taskId && entry.taskId !== '' && !isSectionAttribution;

                                                // For section attributions, prioritize sectionName (full path) over taskName (section name only)
                                                // Convert '>' to '\' for display (dropdown uses '>' format, UI uses '\' format)
                                                const sectionDisplayValue = isSectionAttribution
                                                    ? ((entry.sectionName || entry.taskName || 'No Section').replace(/ > /g, ' \\'))
                                                    : (isTaskAttribution
                                                        ? (entry.sectionName ? entry.sectionName.replace(/ > /g, ' \\') : 'No Section')
                                                        : 'No Section');

                                                return (
                                                    <>
                                                        <div className="section-row">
                                                            Section: <span className="section-name">
                                                                {sectionDisplayValue}
                                                            </span>
                                                        </div>
                                                        <div className="task-row">
                                                            Task: <span className="task-name">
                                                                {isSectionAttribution
                                                                    ? 'No Task'
                                                                    : (isTaskAttribution
                                                                        ? (entry.taskName || 'No Task')
                                                                        : 'No Task')
                                                                }
                                                            </span>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                        <div className="entry-actions">
                                            {writePermission && (
                                                <>
                                                    {deleteConfirmId === entry.id ? (
                                                        <div className="delete-confirm-actions">
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="error"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveEntryFromLocalList(entry.id);
                                                                    setDeleteConfirmId(null);
                                                                }}
                                                                sx={{ minWidth: 'auto', padding: '2px 8px', fontSize: '0.75rem' }}
                                                            >
                                                                Confirm
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setDeleteConfirmId(null);
                                                                }}
                                                                sx={{ minWidth: 'auto', padding: '2px 8px', fontSize: '0.75rem', ml: 0.5 }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <IconButton
                                                                size="small"
                                                                className="edit-btn"
                                                                onClick={(e) => { e.stopPropagation(); handleEditEntry(entry); }}
                                                                title={isApproved ? "Cannot edit approved entry" : "Edit entry"}
                                                                disabled={isApproved}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                className="delete-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setDeleteConfirmId(entry.id);
                                                                }}
                                                                title={isApproved ? "Cannot delete approved entry" : "Delete entry"}
                                                                disabled={isApproved}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default TimeEntryForm;
